import React, { useState, useEffect } from "react";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Box,
  Rating,
  Chip,
  Divider,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Stack
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { api } from "common";
import { colors } from "../components/Theme/WebTheme";
import moment from "moment/min/moment-with-locales";
import { makeStyles } from "@mui/styles";
import { WEB_SECONDORY_COLOR, WEB_MAIN_COLOR, FONT_FAMILY } from "../common/sharedFunctions";
import { getLangKey } from "common/src/other/getLangKey";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TripOriginIcon from "@mui/icons-material/TripOrigin";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RouteIcon from "@mui/icons-material/Route";
import PaymentIcon from "@mui/icons-material/Payment";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import StarIcon from "@mui/icons-material/Star";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EditIcon from "@mui/icons-material/Edit";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "24px",
    maxWidth: "1400px",
    margin: "0 auto",
    fontFamily: FONT_FAMILY,
  },
  headerCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    padding: "20px 24px",
    marginBottom: "24px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
  },
  sectionCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    marginBottom: "24px",
    overflow: "hidden",
    border: "1px solid #F3F4F6",
  },
  cardHeader: {
    padding: "16px 20px",
    backgroundColor: "#F9FAFB",
    borderBottom: "1px solid #EEF2F6",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardTitle: {
    fontSize: "17px !important",
    fontWeight: "700 !important",
    color: "#111827",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 0",
    borderBottom: "1px solid #F3F4F6",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  infoLabel: {
    fontSize: "14px !important",
    color: "#6B7280",
    fontWeight: "500 !important",
  },
  infoValue: {
    fontSize: "14px !important",
    color: "#111827",
    fontWeight: "600 !important",
    textAlign: "right",
  },
  metricBox: {
    backgroundColor: "#F9FAFB",
    borderRadius: "12px",
    padding: "14px 16px",
    textAlign: "center",
    border: "1px solid #EEF2F6",
  },
  locationNode: {
    display: "flex",
    gap: "16px",
    position: "relative",
  },
  timelineLine: {
    position: "absolute",
    left: "15px",
    top: "32px",
    bottom: "-24px",
    width: "2px",
    backgroundColor: "#E5E7EB",
    borderStyle: "dashed",
  },
  avatarWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px 0 10px 0",
  },
  userAvatar: {
    width: 90,
    height: 90,
    border: "3px solid #F3F4F6",
    boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
  },
  copyBtn: {
    padding: "4px !important",
    marginLeft: "4px !important",
    color: "#9CA3AF !important",
    "&:hover": {
      color: "#4B5563 !important",
    },
  },
  thumbnailImage: {
    width: "100%",
    height: "140px",
    objectFit: "cover",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "scale(1.02)",
    },
  },
}));

function BookingDetails() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const dispatch = useDispatch();
  const { updateBooking } = api;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const settings = useSelector((state) => state.settingsdata.settings);
  const classes = useStyles();
  const role = useSelector((state) => state.auth.profile.usertype);
  const { state } = useLocation();
  const [copiedKey, setCopiedKey] = useState("");
  const [previewImage, setPreviewImage] = useState(null);

  // Admin Status Edit State
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [editStatus, setEditStatus] = useState("");
  const [editPaymentStatus, setEditPaymentStatus] = useState("");
  const [editPaymentMode, setEditPaymentMode] = useState("");
  const [editCustomerPaid, setEditCustomerPaid] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [isSaving, setIsSaving] = useState(false);

  const MAIN_COLOR = WEB_MAIN_COLOR(settings?.appCat);
  const SECONDORY_COLOR = WEB_SECONDORY_COLOR(settings?.appCat);

  function formatAmount(value, decimal, country) {
    const number = parseFloat(value || 0);
    if (country === "Vietnam") {
      return number.toLocaleString("vi-VN", {
        minimumFractionDigits: decimal || 2,
        maximumFractionDigits: decimal || 2,
      });
    } else {
      return number.toLocaleString("en-US", {
        minimumFractionDigits: decimal || 2,
        maximumFractionDigits: decimal || 2,
      });
    }
  }

  function getCurrency(amount) {
    if (amount === undefined || amount === null || amount === "") return "-";
    const formatted = formatAmount(amount, settings?.decimal, settings?.country);
    return settings?.swipe_symbol
      ? `${formatted} ${settings?.symbol || "₦"}`
      : `${settings?.symbol || "₦"} ${formatted}`;
  }

  function formatDuration(seconds) {
    if (!seconds || isNaN(seconds)) return null;
    const totalSec = parseInt(seconds, 10);
    if (totalSec < 60) return `${totalSec}s`;
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    if (mins < 60) {
      return secs > 0 ? `${mins}m ${secs}s` : `${mins} min`;
    }
    const hrs = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hrs}h ${remainingMins}m`;
  }

  const handleCopy = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(""), 2000);
  };

  const handleOpenStatusModal = () => {
    setEditStatus(data.status || "NEW");
    setEditPaymentStatus(data.payment_status || (data.status === "PAID" || data.status === "COMPLETE" ? "PAID" : "PENDING"));
    setEditPaymentMode(data.payment_mode || "opay");
    setEditCustomerPaid(data.customer_paid !== undefined ? String(data.customer_paid) : String(data.trip_cost || data.estimate || ""));
    setStatusModalOpen(true);
  };

  const handleSaveStatus = async (presetStatus = null, presetPaymentStatus = null) => {
    setIsSaving(true);
    const targetStatus = presetStatus || editStatus;
    const targetPaymentStatus = presetPaymentStatus || editPaymentStatus;
    
    let updated = {
      ...data,
      status: targetStatus,
      payment_status: targetPaymentStatus,
      payment_mode: editPaymentMode,
    };

    if (targetPaymentStatus === "PAID" || targetStatus === "COMPLETE" || targetStatus === "PAID") {
      updated.payment_status = "PAID";
      updated.prepaid = true;
      updated.payableAmount = 0;
      const paidAmt = parseFloat(editCustomerPaid) || parseFloat(data.customer_paid) || parseFloat(data.trip_cost) || parseFloat(data.estimate) || 0;
      updated.customer_paid = paidAmt;
    }

    try {
      await dispatch(updateBooking(updated));
      setData(updated);
      setStatusModalOpen(false);
      setSnackbar({
        open: true,
        message: `Booking #${data.reference || data.id} status updated to ${targetStatus}!`,
        severity: "success"
      });
    } catch (err) {
      console.error("Error updating booking status:", err);
      setSnackbar({
        open: true,
        message: "Failed to update booking status. Please try again.",
        severity: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (bookinglistdata.bookings) {
      const booking = bookinglistdata.bookings.find(
        (item) => item.id === id.toString()
      );

      if (booking) {
        setData(booking);
      } else {
        navigate("/404");
      }
    }
  }, [bookinglistdata.bookings, id, navigate]);

  if (!data) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="textSecondary">
          {t("loading") || "Loading details..."}
        </Typography>
      </Box>
    );
  }

  const getStatusChip = (status) => {
    switch (status) {
      case "COMPLETE":
      case "PAID":
        return (
          <Chip
            icon={<CheckCircleIcon style={{ color: "#FFFFFF", fontSize: 18 }} />}
            label={t("COMPLETE")}
            sx={{
              backgroundColor: "#10B981",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "13px",
              px: 1,
              py: 0.5,
              borderRadius: "20px",
            }}
          />
        );
      case "CANCELLED":
        return (
          <Chip
            icon={<CancelIcon style={{ color: "#FFFFFF", fontSize: 18 }} />}
            label={t("CANCELLED")}
            sx={{
              backgroundColor: "#EF4444",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "13px",
              px: 1,
              py: 0.5,
              borderRadius: "20px",
            }}
          />
        );
      case "ACCEPTED":
      case "STARTED":
      case "ARRIVED":
      case "REACHED":
        return (
          <Chip
            icon={<HourglassEmptyIcon style={{ color: "#FFFFFF", fontSize: 18 }} />}
            label={t(status)}
            sx={{
              backgroundColor: "#2563EB",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "13px",
              px: 1,
              py: 0.5,
              borderRadius: "20px",
            }}
          />
        );
      default:
        return (
          <Chip
            label={t(status) || status}
            sx={{
              backgroundColor: "#F59E0B",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "13px",
              px: 1,
              py: 0.5,
              borderRadius: "20px",
            }}
          />
        );
    }
  };

  const getVehicleIcon = (type) => {
    const tLower = (type || "").toLowerCase();
    if (tLower.includes("bike") || tLower.includes("motor") || tLower.includes("scooter")) {
      return <TwoWheelerIcon sx={{ color: MAIN_COLOR, fontSize: 20 }} />;
    }
    if (tLower.includes("truck") || tLower.includes("van") || tLower.includes("lorry")) {
      return <LocalShippingIcon sx={{ color: MAIN_COLOR, fontSize: 20 }} />;
    }
    return <DirectionsCarIcon sx={{ color: MAIN_COLOR, fontSize: 20 }} />;
  };

  return (
    <Box className={classes.root} dir={isRTL === "rtl" ? "rtl" : "ltr"}>
      {/* Top Navigation Bar */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/bookings", { state: { pageNo: state?.pageNo } })}
          sx={{
            color: "#374151",
            fontWeight: 600,
            textTransform: "none",
            fontSize: "15px",
            borderRadius: "8px",
            px: 2,
            py: 0.8,
            backgroundColor: "#FFFFFF",
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
            "&:hover": { backgroundColor: "#F3F4F6" },
          }}
        >
          {t("go_back") || "Back to Bookings"}
        </Button>
      </Box>

      {/* Main Header Banner */}
      <Card className={classes.headerCard}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#111827" }}>
              {data.reference ? `Order #${data.reference}` : `Booking #${data.id}`}
            </Typography>
            {getStatusChip(data.status)}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              ID: <span style={{ fontWeight: 600, color: "#374151" }}>{data.id}</span>
            </Typography>
            <Tooltip title={copiedKey === "id" ? "Copied!" : "Copy Booking ID"}>
              <IconButton size="small" onClick={() => handleCopy(data.id, "id")} className={classes.copyBtn}>
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ color: "#9CA3AF" }}>•</Typography>
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              {data.tripdate ? moment(data.tripdate).format("llll") : moment(data.booking_from_date).format("llll")}
            </Typography>
          </Box>
        </Box>

        {/* Quick Summary Pill & Admin Actions */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          {(role === "admin" || role === "fleetadmin") && (
            <Button
              variant="contained"
              size="medium"
              startIcon={<EditIcon />}
              onClick={handleOpenStatusModal}
              sx={{
                backgroundColor: "#2563EB",
                color: "#FFFFFF",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "10px",
                px: 2.2,
                py: 0.8,
                fontSize: "14px",
                boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
                "&:hover": { backgroundColor: "#1D4ED8" },
              }}
            >
              {t("change_status") || "Update Status"}
            </Button>
          )}

          {data.payment_mode && (
            <Chip
              icon={<PaymentIcon style={{ fontSize: 16 }} />}
              label={data.payment_mode.toUpperCase()}
              variant="outlined"
              sx={{ fontWeight: 600, borderColor: "#E5E7EB", color: "#374151" }}
            />
          )}
          <Box sx={{ textAlign: isRTL === "rtl" ? "left" : "right" }}>
            <Typography variant="caption" sx={{ color: "#6B7280", display: "block", fontWeight: 500 }}>
              {t("trip_cost") || "Total Amount"}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: MAIN_COLOR }}>
              {getCurrency(data.customer_paid || data.trip_cost || data.payableAmount)}
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Main 2-Column Responsive Layout */}
      <Grid container spacing={3}>
        {/* LEFT COLUMN: Route, Delivery Specs & Financials (xs=12, lg=8) */}
        <Grid item xs={12} lg={8}>
          {/* Card 1: Route & Location Timeline */}
          <Card className={classes.sectionCard}>
            <Box className={classes.cardHeader}>
              <Typography className={classes.cardTitle}>
                <RouteIcon sx={{ color: MAIN_COLOR }} />
                {t("ride_information") || "Delivery Route & Timeline"}
              </Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              {/* Pickup Point */}
              <Box className={classes.locationNode} sx={{ mb: 3 }}>
                <Box className={classes.timelineLine} />
                <TripOriginIcon sx={{ color: "#10B981", fontSize: 30, zIndex: 1, mt: 0.5, backgroundColor: "#FFFFFF", borderRadius: "50%" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#10B981", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {t("pickup_address") || "Pickup Location"}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#111827", mt: 0.5, fontSize: "15px" }}>
                    {data.pickupAddress || "-"}
                  </Typography>
                  {(data.deliveryPerson || data.deliveryPersonPhone) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1, backgroundColor: "#F9FAFB", p: 1, borderRadius: "8px", width: "fit-content" }}>
                      <PersonIcon sx={{ fontSize: 16, color: "#6B7280" }} />
                      <Typography variant="body2" sx={{ color: "#4B5563", fontWeight: 500 }}>
                        {data.deliveryPerson || "Sender"}
                      </Typography>
                      {data.deliveryPersonPhone && (
                        <>
                          <Typography variant="body2" sx={{ color: "#D1D5DB" }}>|</Typography>
                          <PhoneIcon sx={{ fontSize: 15, color: "#6B7280" }} />
                          <Typography variant="body2" sx={{ color: "#4B5563", fontWeight: 500 }}>
                            {data.deliveryPersonPhone}
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}
                  {data.pickUpInstructions && (
                    <Box sx={{ mt: 1, p: 1.5, backgroundColor: "#FFFBEB", borderLeft: "3px solid #F59E0B", borderRadius: "4px" }}>
                      <Typography variant="caption" sx={{ color: "#92400E", fontWeight: 600, display: "block" }}>
                        {t("pickUpInstructions_web") || "Pickup Instructions"}:
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#78350F" }}>
                        {data.pickUpInstructions}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Dropoff Point */}
              <Box className={classes.locationNode} sx={{ mb: 3 }}>
                <LocationOnIcon sx={{ color: "#EF4444", fontSize: 32, zIndex: 1, mt: 0.5, backgroundColor: "#FFFFFF", borderRadius: "50%" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#EF4444", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {t("drop_address") || "Dropoff Location"}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#111827", mt: 0.5, fontSize: "15px" }}>
                    {data.dropAddress || "-"}
                  </Typography>
                  {(data.otherPerson || data.otherPersonPhone) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1, backgroundColor: "#F9FAFB", p: 1, borderRadius: "8px", width: "fit-content" }}>
                      <PersonIcon sx={{ fontSize: 16, color: "#6B7280" }} />
                      <Typography variant="body2" sx={{ color: "#4B5563", fontWeight: 500 }}>
                        {data.otherPerson || "Receiver"}
                      </Typography>
                      {data.otherPersonPhone && (
                        <>
                          <Typography variant="body2" sx={{ color: "#D1D5DB" }}>|</Typography>
                          <PhoneIcon sx={{ fontSize: 15, color: "#6B7280" }} />
                          <Typography variant="body2" sx={{ color: "#4B5563", fontWeight: 500 }}>
                            {data.otherPersonPhone}
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}
                  {data.deliveryInstructions && (
                    <Box sx={{ mt: 1, p: 1.5, backgroundColor: "#FFFBEB", borderLeft: "3px solid #F59E0B", borderRadius: "4px" }}>
                      <Typography variant="caption" sx={{ color: "#92400E", fontWeight: 600, display: "block" }}>
                        {t("deliveryInstructions") || "Delivery Instructions"}:
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#78350F" }}>
                        {data.deliveryInstructions}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2.5 }} />

              {/* Trip Key Metrics Grid */}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box className={classes.metricBox}>
                    <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 500, display: "block" }}>
                      {t("distance_web") || "Distance"}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827", mt: 0.5 }}>
                      {data.distance ? `${data.distance} ${settings?.convert_to_mile ? t("mile") : t("km")}` : "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box className={classes.metricBox}>
                    <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 500, display: "block" }}>
                      {t("total_time") || "Duration"}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827", mt: 0.5 }}>
                      {formatDuration(data.total_trip_time) || (data.total_trip_time ? `${data.total_trip_time}s` : "-")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box className={classes.metricBox}>
                    <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 500, display: "block" }}>
                      {t("trip_start_time") || "Started At"}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827", mt: 0.5 }}>
                      {data.trip_start_time || "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box className={classes.metricBox}>
                    <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 500, display: "block" }}>
                      {t("trip_end_time") || "Completed At"}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827", mt: 0.5 }}>
                      {data.trip_end_time || "-"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Card 2: Package & Order Specifics */}
          <Card className={classes.sectionCard}>
            <Box className={classes.cardHeader}>
              <Typography className={classes.cardTitle}>
                <Inventory2Icon sx={{ color: MAIN_COLOR }} />
                {t("parcel_type_web") || "Package & Delivery Specifics"}
              </Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box className={classes.infoRow}>
                    <Typography className={classes.infoLabel}>{t("parcel_type_web") || "Parcel Category"}</Typography>
                    <Typography className={classes.infoValue}>
                      {data.parcelTypeSelected ? `${t(getLangKey(data.parcelTypeSelected.description))} (${data.parcelTypeSelected.amount || 0})` : "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className={classes.infoRow}>
                    <Typography className={classes.infoLabel}>{t("parcel_option") || "Weight / Size"}</Typography>
                    <Typography className={classes.infoValue}>
                      {data.optionSelected ? `${t(getLangKey(data.optionSelected.description))} (${data.optionSelected.amount || 0})` : "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className={classes.infoRow}>
                    <Typography className={classes.infoLabel}>{t("otp") || "OTP Verification"}</Typography>
                    <Typography className={classes.infoValue}>
                      {data.otp ? (
                        <Chip label={t("true") || "Enabled"} size="small" sx={{ backgroundColor: "#ECFDF5", color: "#065F46", fontWeight: 600 }} />
                      ) : (
                        <Chip label={t("false") || "Disabled"} size="small" sx={{ backgroundColor: "#F3F4F6", color: "#4B5563" }} />
                      )}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className={classes.infoRow}>
                    <Typography className={classes.infoLabel}>{t("roundTrip") || "Round Trip"}</Typography>
                    <Typography className={classes.infoValue}>
                      {data.roundTrip ? t("yes") : t("no")}
                    </Typography>
                  </Box>
                </Grid>

                {data.reason && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: "#FEF2F2", borderLeft: "3px solid #EF4444", borderRadius: "4px" }}>
                      <Typography variant="caption" sx={{ color: "#991B1B", fontWeight: 700, display: "block" }}>
                        {t("cancellation_reason") || "Cancellation Reason"}:
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#7F1D1D" }}>
                        {t(getLangKey(data.reason)) || data.reason}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Rating & Feedback */}
                {(data.rating || data.feedback) && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, backgroundColor: "#F9FAFB", borderRadius: "10px", border: "1px solid #EEF2F6", mt: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#111827" }}>
                          {t("you_rated_text") || "Customer Rating"}:
                        </Typography>
                        <Rating value={parseFloat(data.rating) || 0} precision={0.5} readOnly size="small" />
                      </Box>
                      {data.feedback && (
                        <Typography variant="body2" sx={{ color: "#4B5563", fontStyle: "italic", mt: 0.5 }}>
                          "{data.feedback}"
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}

                {/* Proof Photos */}
                {(data.pickup_image || data.deliver_image) && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#111827", mt: 1, mb: 1.5 }}>
                      {t("delivery_photos") || "Delivery Proof Photos"}
                    </Typography>
                    <Grid container spacing={2}>
                      {data.pickup_image && (
                        <Grid item xs={6} sm={4}>
                          <Box onClick={() => setPreviewImage(data.pickup_image)} sx={{ position: "relative" }}>
                            <img src={data.pickup_image} alt="Pickup Proof" className={classes.thumbnailImage} />
                            <Chip
                              size="small"
                              icon={<PhotoCameraIcon style={{ fontSize: 14, color: "#FFF" }} />}
                              label={t("take_pickup_image_web") || "Pickup Photo"}
                              sx={{ position: "absolute", bottom: 8, left: 8, backgroundColor: "rgba(0,0,0,0.7)", color: "#FFF", fontSize: "11px" }}
                            />
                          </Box>
                        </Grid>
                      )}
                      {data.deliver_image && (
                        <Grid item xs={6} sm={4}>
                          <Box onClick={() => setPreviewImage(data.deliver_image)} sx={{ position: "relative" }}>
                            <img src={data.deliver_image} alt="Delivery Proof" className={classes.thumbnailImage} />
                            <Chip
                              size="small"
                              icon={<PhotoCameraIcon style={{ fontSize: 14, color: "#FFF" }} />}
                              label={t("take_deliver_image_web") || "Delivery Photo"}
                              sx={{ position: "absolute", bottom: 8, left: 8, backgroundColor: "rgba(0,0,0,0.7)", color: "#FFF", fontSize: "11px" }}
                            />
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Card 3: Financial & Payment Breakdown */}
          <Card className={classes.sectionCard}>
            <Box className={classes.cardHeader}>
              <Typography className={classes.cardTitle}>
                <PaymentIcon sx={{ color: MAIN_COLOR }} />
                {t("payment_info") || "Payment & Fare Breakdown"}
              </Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ maxWidth: "600px", margin: "0 auto" }}>
                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>{t("trip_cost") || "Base Trip Cost"}</Typography>
                  <Typography className={classes.infoValue}>{getCurrency(data.trip_cost)}</Typography>
                </Box>
                {data.discount && parseFloat(data.discount) > 0 ? (
                  <Box className={classes.infoRow}>
                    <Typography className={classes.infoLabel}>{t("discount_ammount") || "Discount"}</Typography>
                    <Typography className={classes.infoValue} sx={{ color: "#10B981 !important" }}>
                      -{getCurrency(data.discount)}
                    </Typography>
                  </Box>
                ) : null}
                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>{t("convenience_fee") || "Platform Convenience Fee"}</Typography>
                  <Typography className={classes.infoValue}>{getCurrency(data.convenience_fees)}</Typography>
                </Box>
                {data.fleetCommission && parseFloat(data.fleetCommission) > 0 ? (
                  <Box className={classes.infoRow}>
                    <Typography className={classes.infoLabel}>{t("fleet_admin_comission") || "Fleet Admin Commission"}</Typography>
                    <Typography className={classes.infoValue}>{getCurrency(data.fleetCommission)}</Typography>
                  </Box>
                ) : null}
                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>{t("driver_share") || "Driver Share / Earning"}</Typography>
                  <Typography className={classes.infoValue} sx={{ color: "#2563EB !important" }}>
                    {getCurrency(data.driver_share)}
                  </Typography>
                </Box>
                {data.tipamount && parseFloat(data.tipamount) > 0 ? (
                  <Box className={classes.infoRow}>
                    <Typography className={classes.infoLabel}>{t("tipamount") || "Driver Tip"}</Typography>
                    <Typography className={classes.infoValue}>{getCurrency(data.tipamount)}</Typography>
                  </Box>
                ) : null}
                {data.cancellationFee && parseFloat(data.cancellationFee) > 0 ? (
                  <Box className={classes.infoRow}>
                    <Typography className={classes.infoLabel}>{t("cancellationFee") || "Cancellation Fee"}</Typography>
                    <Typography className={classes.infoValue} sx={{ color: "#EF4444 !important" }}>
                      {getCurrency(data.cancellationFee)}
                    </Typography>
                  </Box>
                ) : null}

                <Divider sx={{ my: 2 }} />

                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>{t("payment_mode_web") || "Payment Method"}</Typography>
                  <Typography className={classes.infoValue}>
                    <Chip
                      label={(data.payment_mode || "cash").toUpperCase()}
                      size="small"
                      sx={{ backgroundColor: "#F3F4F6", fontWeight: 700, color: "#111827" }}
                    />
                  </Typography>
                </Box>
                {data.gateway && (
                  <Box className={classes.infoRow}>
                    <Typography className={classes.infoLabel}>{t("payment_gateway") || "Gateway"}</Typography>
                    <Typography className={classes.infoValue}>{data.gateway}</Typography>
                  </Box>
                )}

                {/* Total Paid Box */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: "#F9FAFB",
                    borderRadius: "12px",
                    border: "1px solid #E5E7EB",
                    mt: 2.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827" }}>
                      {t("Customer_paid") || "Total Customer Paid"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6B7280" }}>
                      {data.payment_mode === "cash" ? "Collected in Cash by Rider" : "Paid via Online Gateway / Wallet"}
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#111827" }}>
                    {getCurrency(data.customer_paid || data.payableAmount || data.trip_cost)}
                  </Typography>
                </Paper>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* RIGHT COLUMN: Driver & Customer Cards (xs=12, lg=4) */}
        <Grid item xs={12} lg={4}>
          {/* Assigned Driver Card */}
          <Card className={classes.sectionCard}>
            <Box className={classes.cardHeader}>
              <Typography className={classes.cardTitle}>
                <TwoWheelerIcon sx={{ color: MAIN_COLOR }} />
                {t("driver_info") || "Driver Information"}
              </Typography>
              {data.carType && (
                <Chip
                  icon={getVehicleIcon(data.carType)}
                  label={data.carType.toUpperCase()}
                  size="small"
                  sx={{ backgroundColor: "#EFF6FF", color: "#1D4ED8", fontWeight: 700 }}
                />
              )}
            </Box>
            <CardContent sx={{ p: 3 }}>
              {data.driver ? (
                <>
                  <Box className={classes.avatarWrapper}>
                    {data.driver_image ? (
                      <Avatar src={data.driver_image} alt={data.driver_name} className={classes.userAvatar} />
                    ) : (
                      <Avatar className={classes.userAvatar} sx={{ backgroundColor: "#E5E7EB", color: "#6B7280" }}>
                        <AccountCircleIcon sx={{ width: 70, height: 70 }} />
                      </Avatar>
                    )}
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827", mt: 1.5 }}>
                      {data.driver_name || "Assigned Driver"}
                    </Typography>
                    {data.vehicle_number && (
                      <Chip
                        label={data.vehicle_number}
                        size="small"
                        sx={{
                          mt: 0.5,
                          backgroundColor: "#FEF3C7",
                          color: "#92400E",
                          fontWeight: 700,
                          letterSpacing: 1,
                          border: "1px solid #FDE68A",
                        }}
                      />
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box className={classes.infoRow}>
                    <Typography className={classes.infoLabel}>
                      <PhoneIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
                      {t("driver_contact") || "Contact"}
                    </Typography>
                    <Typography className={classes.infoValue}>
                      {data.driver_contact ? (
                        <a href={`tel:${data.driver_contact}`} style={{ color: "#2563EB", textDecoration: "none" }}>
                          {data.driver_contact}
                        </a>
                      ) : (
                        "-"
                      )}
                    </Typography>
                  </Box>

                  {data.driver_email && (
                    <Box className={classes.infoRow}>
                      <Typography className={classes.infoLabel}>
                        <EmailIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
                        {t("driver_email") || "Email"}
                      </Typography>
                      <Typography className={classes.infoValue} sx={{ wordBreak: "break-all" }}>
                        {data.driver_email}
                      </Typography>
                    </Box>
                  )}

                  <Box className={classes.infoRow}>
                    <Typography className={classes.infoLabel}>{t("driver_id") || "Driver ID"}</Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography className={classes.infoValue} sx={{ maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {data.driver}
                      </Typography>
                      <Tooltip title={copiedKey === "driver" ? "Copied!" : "Copy Driver ID"}>
                        <IconButton size="small" onClick={() => handleCopy(data.driver, "driver")} className={classes.copyBtn}>
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {data.driverDeviceId && (
                    <Box className={classes.infoRow}>
                      <Typography className={classes.infoLabel}>{t("device_id") || "Device ID"}</Typography>
                      <Typography className={classes.infoValue} sx={{ fontSize: "12px !important", color: "#6B7280" }}>
                        {data.driverDeviceId}
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="textSecondary">
                    {t("no_driver_assigned") || "No driver assigned yet."}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Customer Card */}
          <Card className={classes.sectionCard}>
            <Box className={classes.cardHeader}>
              <Typography className={classes.cardTitle}>
                <PersonIcon sx={{ color: MAIN_COLOR }} />
                {t("customer_info") || "Customer Information"}
              </Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              <Box className={classes.avatarWrapper}>
                {data.customer_image ? (
                  <Avatar src={data.customer_image} alt={data.customer_name} className={classes.userAvatar} />
                ) : (
                  <Avatar className={classes.userAvatar} sx={{ backgroundColor: "#E5E7EB", color: "#6B7280" }}>
                    <AccountCircleIcon sx={{ width: 70, height: 70 }} />
                  </Avatar>
                )}
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827", mt: 1.5 }}>
                  {data.customer_name || "Customer"}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>
                  <PhoneIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
                  {t("customer_contact") || "Contact"}
                </Typography>
                <Typography className={classes.infoValue}>
                  {data.customer_contact ? (
                    <a href={`tel:${data.customer_contact}`} style={{ color: "#2563EB", textDecoration: "none" }}>
                      {data.customer_contact}
                    </a>
                  ) : (
                    "-"
                  )}
                </Typography>
              </Box>

              {data.customer_email && (
                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>
                    <EmailIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
                    {t("customer_email") || "Email"}
                  </Typography>
                  <Typography className={classes.infoValue} sx={{ wordBreak: "break-all" }}>
                    {data.customer_email}
                  </Typography>
                </Box>
              )}

              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>{t("customer_id") || "Customer ID"}</Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography className={classes.infoValue} sx={{ maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {data.customer || "-"}
                  </Typography>
                  {data.customer && (
                    <Tooltip title={copiedKey === "customer" ? "Copied!" : "Copy Customer ID"}>
                      <IconButton size="small" onClick={() => handleCopy(data.customer, "customer")} className={classes.copyBtn}>
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Image Preview Modal */}
      <Dialog open={Boolean(previewImage)} onClose={() => setPreviewImage(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t("preview") || "Photo Preview"}
          </Typography>
          <IconButton onClick={() => setPreviewImage(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", p: 2 }}>
          {previewImage && (
            <img src={previewImage} alt="Preview" style={{ maxWidth: "100%", maxHeight: "75vh", borderRadius: "8px" }} />
          )}
        </DialogContent>
      </Dialog>

      {/* Admin Status & Delivery Management Dialog */}
      <Dialog
        open={statusModalOpen}
        onClose={() => !isSaving && setStatusModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", p: 1 }
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AdminPanelSettingsIcon sx={{ color: "#2563EB" }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {t("update_booking_status") || "Manage Delivery & Payment Status"}
            </Typography>
          </Box>
          <IconButton onClick={() => setStatusModalOpen(false)} disabled={isSaving}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          {/* Quick Presets */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1 }}>
              ⚡ Quick Actions
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                color="success"
                size="small"
                startIcon={<DoneAllIcon />}
                disabled={isSaving}
                onClick={() => handleSaveStatus("COMPLETE", "PAID")}
                sx={{
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "8px",
                  borderColor: "#10B981",
                  color: "#059669",
                  "&:hover": { backgroundColor: "#ECFDF5", borderColor: "#059669" }
                }}
              >
                Mark as Completed & Paid
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<CancelIcon />}
                disabled={isSaving}
                onClick={() => handleSaveStatus("CANCELLED", editPaymentStatus)}
                sx={{
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "8px",
                  borderColor: "#EF4444",
                  color: "#DC2626",
                  "&:hover": { backgroundColor: "#FEF2F2", borderColor: "#DC2626" }
                }}
              >
                Mark as Cancelled
              </Button>
            </Stack>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Form Fields */}
          <Stack spacing={2.5}>
            {/* Booking / Delivery Status */}
            <FormControl fullWidth size="medium">
              <InputLabel id="status-select-label" sx={{ fontWeight: 600 }}>Delivery / Order Status</InputLabel>
              <Select
                labelId="status-select-label"
                value={editStatus}
                label="Delivery / Order Status"
                onChange={(e) => setEditStatus(e.target.value)}
                disabled={isSaving}
                sx={{ borderRadius: "10px", fontWeight: 600 }}
              >
                <MenuItem value="NEW">NEW (Order Created)</MenuItem>
                <MenuItem value="ACCEPTED">ACCEPTED (Driver Assigned)</MenuItem>
                <MenuItem value="ARRIVED">ARRIVED (Driver at Pickup)</MenuItem>
                <MenuItem value="STARTED">STARTED (In Transit / Delivery Active)</MenuItem>
                <MenuItem value="REACHED">REACHED (Arrived at Destination)</MenuItem>
                <MenuItem value="PENDING">PENDING (Awaiting Final Settlement)</MenuItem>
                <MenuItem value="COMPLETE">COMPLETE (Delivery Finished)</MenuItem>
                <MenuItem value="CANCELLED">CANCELLED (Order Voided)</MenuItem>
              </Select>
            </FormControl>

            {/* Payment Status */}
            <FormControl fullWidth size="medium">
              <InputLabel id="payment-status-select-label" sx={{ fontWeight: 600 }}>Payment Status</InputLabel>
              <Select
                labelId="payment-status-select-label"
                value={editPaymentStatus}
                label="Payment Status"
                onChange={(e) => setEditPaymentStatus(e.target.value)}
                disabled={isSaving}
                sx={{ borderRadius: "10px", fontWeight: 600 }}
              >
                <MenuItem value="PAID">PAID (Payment Verified & Settled)</MenuItem>
                <MenuItem value="PENDING">PENDING (Payment Pending)</MenuItem>
                <MenuItem value="UNPAID">UNPAID (Not Paid)</MenuItem>
                <MenuItem value="REFUNDED">REFUNDED (Money Returned)</MenuItem>
              </Select>
            </FormControl>

            {/* Payment Mode */}
            <FormControl fullWidth size="medium">
              <InputLabel id="payment-mode-select-label" sx={{ fontWeight: 600 }}>Payment Gateway / Mode</InputLabel>
              <Select
                labelId="payment-mode-select-label"
                value={editPaymentMode}
                label="Payment Gateway / Mode"
                onChange={(e) => setEditPaymentMode(e.target.value)}
                disabled={isSaving}
                sx={{ borderRadius: "10px", fontWeight: 600 }}
              >
                <MenuItem value="opay">OPay</MenuItem>
                <MenuItem value="card">Card (Mastercard / Visa / Verve)</MenuItem>
                <MenuItem value="cash">Cash on Delivery</MenuItem>
                <MenuItem value="wallet">User Wallet</MenuItem>
              </Select>
            </FormControl>

            {/* Customer Paid Amount */}
            <TextField
              label="Customer Paid Amount"
              type="number"
              fullWidth
              value={editCustomerPaid}
              onChange={(e) => setEditCustomerPaid(e.target.value)}
              disabled={isSaving}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1, color: "#6B7280", fontWeight: 600 }}>{settings?.symbol || "₦"}</Typography>
              }}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "10px" }
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => setStatusModalOpen(false)}
            disabled={isSaving}
            sx={{
              fontWeight: 600,
              color: "#6B7280",
              textTransform: "none",
              borderRadius: "8px",
              px: 2
            }}
          >
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSaveStatus()}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
            sx={{
              backgroundColor: "#2563EB",
              color: "#FFFFFF",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
              "&:hover": { backgroundColor: "#1D4ED8" }
            }}
          >
            {isSaving ? "Saving Changes..." : (t("save_changes") || "Save Changes")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Toast */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: "10px", fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default BookingDetails;